import React, { useEffect, useState } from 'react';
import {
  Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtDate, panelPost, useAdminGet,
} from './kit';
import type { AdminTargetIn } from './adminNav';
import CtaTab, { type CtaKind } from './leads/CtaTab';
import ProspectsTab from './leads/prospects/ProspectsTab';

/**
 * Leads & Marketing group — admin_panel_v2 Tab 19 (Public Leads: newsletter,
 * whitepaper, COMET) + the Concierge Requests manager (Tab 5) + the early
 * access / List-Your-IP tables that only had capture endpoints before, and
 * (2026-10-08) the Contact University / Talk to Concierge lists with their
 * own team e-mail switches (leads/CtaTab), and (2026-10-18) Prospects first:
 * every non-paying account ranked by intent + activity with a suggested nudge
 * (leads/prospects/*, backend admin_prospects.py).
 */

const TABS = ['Prospects', 'COMET', 'Whitepaper', 'Newsletter', 'Concierge', 'Contact University', 'Talk to Concierge',
  'Interest', 'Early access', 'List Your IP'];
const CTA_TABS: Record<string, CtaKind> = {
  'Contact University': 'contact_university', 'Talk to Concierge': 'talk_to_concierge',
};
/** A deep link's tab=: the label itself or its snake_case ("Talk to Concierge" → talk_to_concierge). */
const tabKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const tabFromLink = (raw: string) => (raw ? TABS.find(t => t === raw || tabKey(t) === tabKey(raw)) : undefined);

/**
 * Deep link: /admin?group=leads&tab=contact_university (or talk_to_concierge,
 * or any tab's name / snake_case). &user=<uuid> narrows those two lists to
 * that person — with no tab it opens Contact University.
 */
const LeadsPanel: React.FC<{ initialTab?: string; target?: AdminTargetIn }> = ({ initialTab, target }) => {
  // Read straight into the first render too, so a deep link does not fetch
  // the COMET tab (or an unfiltered list) on its way to the one it names.
  const linkUser = () => String(target?.user || '').trim();
  const linkTab = () => tabFromLink(String(target?.tab || '').trim()) || (linkUser() ? 'Contact University' : undefined);
  const linkFocus = (t?: string) =>
    (target && linkUser() && t && CTA_TABS[t] ? { user: linkUser(), n: target.nonce } : null);
  const [tab, setTab] = useState(() => linkTab() || tabFromLink(initialTab || '') || TABS[0]);
  const [focus, setFocus] = useState<{ user: string; n: string } | null>(() => linkFocus(linkTab()));
  useEffect(() => {
    const t = tabFromLink(initialTab || '');
    if (t) setTab(t);
  }, [initialTab]);
  useEffect(() => {
    if (!target) return;
    const t = linkTab();
    if (t) setTab(t);
    setFocus(linkFocus(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  return (
    <div className="flex flex-col gap-4">
      {/* A tab click is a fresh start: the linked person is not re-applied. */}
      <SubTabs tabs={TABS} active={tab} onChange={t => { setFocus(null); setTab(t); }} />
      {tab === 'Prospects' && <ProspectsTab />}
      {tab === 'COMET' && <Comet />}
      {tab === 'Whitepaper' && <Whitepaper />}
      {tab === 'Newsletter' && <Newsletter />}
      {tab === 'Concierge' && <Concierge />}
      {CTA_TABS[tab] && <CtaTab key={tab} kind={CTA_TABS[tab]} focus={focus} />}
      {tab === 'Interest' && <Interest />}
      {tab === 'Early access' && <SimpleTable action="early_access" title="Early-access signups" />}
      {tab === 'List Your IP' && <SimpleTable action="list_your_ip" title="List-Your-IP leads" />}
    </div>
  );
};

// ---------------------------------------------------------------------------
const Comet: React.FC = () => {
  const comet = useAdminGet('leads', 'comet');
  const [q, setQ] = useState('');
  const [conf, setConf] = useState('All');
  const [selected, setSelected] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const rows: any[] = comet.data?.rows || [];
  const filtered = rows.filter(r => {
    if (conf !== 'All' && (r.confirmation_status || 'pending') !== conf) return false;
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      return [r.work_email, r.full_name, r.organisation, r.reference_code]
        .some(v => (v || '').toLowerCase().includes(n));
    }
    return true;
  });
  const failedConf = rows.filter(r => r.confirmation_status === 'failed').length;
  const detail = rows.find(r => r.id === selected);

  const act = async (action: string, body: Record<string, any>, okText: string) => {
    const { data, error } = await panelPost('leads', action, body);
    if (error || data?.success === false) setMsg({ kind: 'error', text: error || data?.error || 'Failed' });
    else { setMsg({ kind: 'ok', text: okText }); comet.reload(); }
  };

  return (
    <div className="flex flex-col gap-4">
      {comet.data && (
        <KPIRow>
          <KPI label="Submissions" value={rows.length} />
          <KPI label="Total offers" value={rows.reduce((a, r) => a + (r.total_offers || 0), 0)} />
          <KPI label="Organisations" value={new Set(rows.map(r => r.organisation)).size} />
          <KPI label="Confirmation failures" value={failedConf} hint={failedConf ? 'needs resend' : undefined} />
          <KPI label="New / unactioned" value={rows.filter(r => (r.status || 'new') === 'new').length} />
        </KPIRow>
      )}
      {failedConf > 0 && (
        <Msg kind="error">
          {failedConf} submitter confirmation email(s) failed — those people have no tracking
          reference. Open the row and resend.
        </Msg>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="COMET Phase 1 — expressions of interest"
        right={<Btn small onClick={comet.reload}>Refresh</Btn>}>
        <div className="flex gap-2 flex-wrap">
          <TextInput value={q} onChange={setQ} placeholder="Search email / name / organisation / reference…" wide />
          <Select value={conf} onChange={setConf} options={['All', 'sent', 'failed', 'pending']}
            labels={{ All: 'All confirmations' }} />
        </div>
        {comet.loading ? <Loading /> : (
          <DataTable rows={filtered} csvName="comet_interest"
            columns={['submitted_at', 'reference_code', 'organisation', 'full_name', 'work_email',
              'organisation_type', 'total_offers', 'confirmation_status', 'internal_status', 'status']}
            empty="No COMET expressions of interest yet."
            actions={r => <Btn small onClick={() => setSelected(r.id)}>Open</Btn>} />
        )}
        {(comet.data?.test_rows || 0) > 0 && (
          <div className="flex items-center gap-2 text-xs text-grayscale-1100">
            {comet.data.test_rows} smoke-test row(s) present (form_source = comet_smoke_test).
            <DangerBtn small onConfirm={() => act('comet_purge_tests', {}, 'Smoke-test rows removed.')}>
              Delete test rows
            </DangerBtn>
          </div>
        )}
      </Section>
      {detail && <CometDetail row={detail} stages={comet.data?.portfolio_stages || []}
        onClose={() => setSelected(null)} act={act} />}
    </div>
  );
};

const CometDetail: React.FC<{
  row: any; stages: { key: string; label: string; hint: string }[];
  onClose: () => void;
  act: (action: string, body: Record<string, any>, okText: string) => Promise<void>;
}> = ({ row, stages, onClose, act }) => {
  const [status, setStatus] = useState(row.status || 'new');
  const [notes, setNotes] = useState(row.internal_notes || '');
  const badge = (label: string, v: string, err?: string) => (
    <div className={`text-sm rounded-lg px-3 py-2 border ${v === 'sent'
      ? 'text-green-700 bg-green-50 border-green-200'
      : v === 'failed' ? 'text-red-600 bg-red-50 border-red-200'
        : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
      {label}: <b>{v || 'pending'}</b>{err && <div className="text-xs mt-1">{err}</div>}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">{row.reference_code}</h2>
            <div className="text-xs text-grayscale-1100">Submitted {fmtDate(row.submitted_at)}</div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        <Section title="About them">
          <div className="text-sm text-grayscale-1300 flex flex-col gap-1">
            <div><b>{row.full_name}</b> · {row.designation || '—'}</div>
            <div>{row.organisation} ({row.organisation_type_other || row.organisation_type || '—'})</div>
            <div>{row.work_email} · {row.phone || 'no phone'}</div>
          </div>
        </Section>
        <Section title="Portfolio">
          <div className="text-sm text-grayscale-1300 flex flex-col gap-1">
            {stages.map(s => (
              <div key={s.key} className="flex justify-between gap-4">
                <span title={s.hint}>{s.label}</span><b>{row[s.key] || 0}</b>
              </div>
            ))}
            <div className="flex justify-between gap-4 border-t border-grayscale-300 pt-1">
              <span>Total offers</span><b>{row.total_offers || 0}</b>
            </div>
            <div className="text-xs text-grayscale-1100 mt-1">
              Data points: {(row.data_points || []).join(', ') || '—'}
            </div>
          </div>
        </Section>
        <Section title="Email delivery">
          <div className="grid grid-cols-2 gap-2">
            {badge('Submitter confirmation', row.confirmation_status, row.confirmation_error)}
            {badge('Ops notification', row.internal_status, row.internal_error)}
          </div>
          <div>
            <Btn small onClick={() => act('comet_resend', { id: row.id }, `Confirmation resent to ${row.work_email}.`)}>
              Resend confirmation email
            </Btn>
          </div>
        </Section>
        <Section title="Triage">
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={status} onChange={setStatus}
              options={['new', 'contacted', 'qualified', 'listed', 'declined']} />
            <TextInput value={notes} onChange={setNotes} placeholder="Internal notes…" wide />
            <Btn small kind="primary"
              onClick={() => act('comet_update', { id: row.id, status, notes }, 'Status & notes saved.')}>
              Save
            </Btn>
          </div>
          <div className="border-t border-grayscale-300 pt-3">
            <p className="text-xs text-grayscale-1100 mb-2">
              Deleting removes the row permanently — the submitter's tracking reference stops
              resolving. Cannot be undone.
            </p>
            <DangerBtn small onConfirm={() => { act('comet_delete', { id: row.id }, `Deleted ${row.reference_code}.`); onClose(); }}>
              Delete this submission
            </DangerBtn>
          </div>
        </Section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const Whitepaper: React.FC = () => {
  const reqs = useAdminGet('leads', 'whitepaper');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const rows: any[] = reqs.data || [];

  const act = async (action: string, body: Record<string, any>, okText: string) => {
    const { data, error } = await panelPost('leads', action, body);
    if (error || data?.success === false) setMsg({ kind: 'error', text: error || data?.error || 'Failed' });
    else { setMsg({ kind: 'ok', text: okText }); reqs.reload(); }
  };

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Requests" value={rows.length} />
        <KPI label="Sent" value={rows.filter(r => r['Email send'] === 'sent').length} />
        <KPI label="Failed" value={rows.filter(r => r['Email send'] === 'failed').length} />
        <KPI label="Pending" value={rows.filter(r => !r['Email send'] || r['Email send'] === 'pending').length} />
      </KPIRow>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Whitepaper requests" right={<Btn small onClick={reqs.reload}>Refresh</Btn>}>
        {reqs.loading ? <Loading /> : (
          <DataTable rows={rows} searchable csvName="whitepaper_requests"
            empty="No whitepaper requests yet."
            actions={r => (
              <div className="flex gap-1">
                <Btn small title="Resend the whitepaper email"
                  onClick={() => act('whitepaper_resend', { request_id: r._id }, `Whitepaper resent to ${r.Email}.`)}>
                  Resend
                </Btn>
                {r.Status !== 'Granted' && (
                  <Btn small onClick={() => act('whitepaper_status', { request_id: r._id, status: 'Granted' }, 'Marked granted.')}>Grant</Btn>
                )}
                {r.Status !== 'Declined' && (
                  <Btn small onClick={() => act('whitepaper_status', { request_id: r._id, status: 'Declined' }, 'Marked declined.')}>Decline</Btn>
                )}
              </div>
            )} />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
const Newsletter: React.FC = () => {
  const subs = useAdminGet('leads', 'newsletter');
  return (
    <Section title="Newsletter subscribers" right={<Btn small onClick={subs.reload}>Refresh</Btn>}>
      {subs.loading ? <Loading /> : (
        <DataTable rows={subs.data || []} searchable csvName="newsletter_subscribers"
          empty="No subscribers yet." />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const Concierge: React.FC = () => {
  const reqs = useAdminGet('leads', 'concierge');
  const [status, setStatus] = useState('All');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const rows: any[] = (reqs.data || []).filter((r: any) =>
    status === 'All' || r.status === status.toLowerCase());

  const setSt = async (id: string, s: string) => {
    const { error } = await panelPost('leads', 'concierge_status', { id, status: s });
    if (error) setMsg({ kind: 'error', text: error });
    else reqs.reload();
  };
  const del = async (id: string) => {
    const { error } = await panelPost('leads', 'concierge_delete', { id });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: 'Request deleted.' }); reqs.reload(); }
  };

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Requested" value={(reqs.data || []).filter((r: any) => r.status === 'requested').length} />
        <KPI label="Booked" value={(reqs.data || []).filter((r: any) => r.status === 'booked').length} />
        <KPI label="Done" value={(reqs.data || []).filter((r: any) => r.status === 'done').length} />
      </KPIRow>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Concierge call requests"
        right={<Select value={status} onChange={setStatus} options={['All', 'Requested', 'Booked', 'Done']} />}>
        {reqs.loading ? <Loading /> : (
          <DataTable rows={rows} searchable csvName="concierge_requests"
            columns={['name', 'email', 'areas', 'message', 'status', 'slot', 'when']}
            empty="No concierge requests."
            actions={r => (
              <div className="flex gap-1">
                {r.status === 'requested' && <Btn small onClick={() => setSt(r.id, 'booked')}>Book</Btn>}
                {r.status !== 'done' && <Btn small onClick={() => setSt(r.id, 'done')}>Done</Btn>}
                {r.status !== 'requested' && <Btn small onClick={() => setSt(r.id, 'requested')}>Reopen</Btn>}
                <DangerBtn small onConfirm={() => del(r.id)}>Delete</DangerBtn>
              </div>
            )} />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
/**
 * Booking-CTA clicks. Deliberately NOT the Concierge tab above: that lists
 * submitted requests, this lists people who only showed intent — they opened
 * the calendar and may never have booked. One row per person per surface with
 * a click counter, so the table is people, not raw events.
 */
/** Turn the first-click email on or off, and choose who receives it. */
const InterestAlerts: React.FC = () => {
  const cfg = useAdminGet('leads', 'interest_alerts');
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const d: any = cfg.data;
  // null until touched, so the loaded value shows without a second render pass.
  const on = enabled === null ? !!d?.enabled : enabled;
  const recips = to === null ? (d?.recipients || []).join(', ') : to;

  return (
    <Section title="Email alert on a first click"
      right={<Btn small onClick={cfg.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Sent the first time a given person clicks a given CTA — not on repeats,
        which would train everyone to ignore it. Turning this off does not stop
        the logging below; clicks are still recorded either way.
      </p>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {cfg.loading ? <Loading /> : (
        <div className="flex gap-2 items-center flex-wrap mt-2">
          <label className="text-sm flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={on} onChange={e => setEnabled(e.target.checked)} />
            Send alerts
          </label>
          <TextInput value={recips} onChange={setTo}
            placeholder="recipients, comma separated" wide />
          <Btn onClick={async () => {
            const list = recips.split(',').map(x => x.trim()).filter(Boolean);
            const { data, error } = await panelPost('leads', 'set_interest_alerts',
              { enabled: on, recipients: list });
            if (error) setMsg({ kind: 'error', text: error });
            else {
              setMsg({ kind: 'ok', text: data.enabled
                ? `Alerts on — going to ${(data.recipients || []).join(', ')}`
                : 'Alerts off. Clicks are still logged.' });
              setEnabled(null); setTo(null); cfg.reload();
            }
          }}>Save</Btn>
        </div>
      )}
    </Section>
  );
};

const Interest: React.FC = () => {
  const data = useAdminGet('leads', 'interest');
  const [status, setStatus] = useState('All');
  const [source, setSource] = useState('All');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const all: any[] = data.data?.rows || [];
  const totals: Record<string, any> = data.data?.totals || {};
  const rows = all.filter((r: any) =>
    (status === 'All' || r.status === status.toLowerCase())
    && (source === 'All' || r.source_label === source));

  const setSt = async (id: number, s: string) => {
    const { error } = await panelPost('leads', 'interest_status', { id, status: s });
    if (error) setMsg({ kind: 'error', text: error });
    else data.reload();
  };
  const del = async (id: number) => {
    const { error } = await panelPost('leads', 'interest_delete', { id });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: 'Interest row deleted.' }); data.reload(); }
  };

  return (
    <div className="flex flex-col gap-4">
      <InterestAlerts />
      {/* People vs clicks are separate numbers on purpose: one person
          pressing five times is one lead, not five. */}
      <KPIRow>
        {Object.entries(totals).map(([k, t]: [string, any]) => (
          <KPI key={k} label={t.label} value={t.people}
            hint={`${t.clicks} click${t.clicks === 1 ? '' : 's'} total`} />
        ))}
      </KPIRow>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section
        title="Interest — booking CTA clicks (they may never have booked)"
        right={
          <div className="flex gap-2">
            <Select value={source} onChange={setSource}
              options={['All', ...Object.values(totals).map((t: any) => t.label)]} />
            <Select value={status} onChange={setStatus}
              options={['All', 'New', 'Flagged', 'Done', 'Archived']} />
            <Btn small onClick={data.reload}>Refresh</Btn>
          </div>}>
        {data.loading ? <Loading /> : (
          <DataTable rows={rows} searchable csvName="interest_clicks"
            columns={['name', 'email', 'source_label', 'clicks', 'status',
                      'first_at', 'last_at']}
            empty="Nobody has clicked a booking CTA yet."
            actions={(r: any) => (
              <div className="flex gap-1">
                {r.status !== 'flagged' && <Btn small onClick={() => setSt(r.id, 'flagged')}>Flag</Btn>}
                {r.status !== 'done' && <Btn small onClick={() => setSt(r.id, 'done')}>Done</Btn>}
                {r.status !== 'archived' && <Btn small onClick={() => setSt(r.id, 'archived')}>Archive</Btn>}
                {r.status !== 'new' && <Btn small onClick={() => setSt(r.id, 'new')}>Reopen</Btn>}
                <DangerBtn small onConfirm={() => del(r.id)}>Delete</DangerBtn>
              </div>
            )} />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
const SimpleTable: React.FC<{ action: string; title: string }> = ({ action, title }) => {
  const data = useAdminGet('leads', action, {}, [action]);
  return (
    <Section title={title} right={<Btn small onClick={data.reload}>Refresh</Btn>}>
      {data.loading ? <Loading /> : (
        <DataTable rows={data.data || []} searchable csvName={action}
          columns={data.data?.[0]
            ? Object.keys(data.data[0]).filter(k => !['id', 'user_agent', 'ip_address', 'extra'].includes(k))
            : undefined}
          empty="Nothing captured yet." />
      )}
    </Section>
  );
};

export default LeadsPanel;
